import { useState, useEffect, useRef, useMemo } from 'react'
import * as api from '../api'
import { useJobs } from '../jobs'

/* ── Constants ─────────────────────────────────────────────────────────────── */
const EXAM_TYPES = [
  {
    id: 'full',
    label: 'Full Exam',
    desc: 'MCQ + Fill-in-blanks + Subjective',
    icon: 'description',
    color: 'var(--accent-2)',
    bg: 'var(--accent-soft)'
  },
  {
    id: 'mcq_only',
    label: 'MCQ Only',
    desc: 'All multiple choice, 1 mark each',
    icon: 'checklist',
    color: 'var(--amber)',
    bg: 'var(--amber-soft)'
  },
  {
    id: 'objective',
    label: 'Objective Only',
    desc: 'MCQ + Fill-in-blanks only',
    icon: 'check_box',
    color: 'var(--sky)',
    bg: 'var(--sky-soft)'
  },
  {
    id: 'subjective',
    label: 'Subjective Only',
    desc: 'Short & long written answers',
    icon: 'edit_note',
    color: 'var(--purple)',
    bg: 'var(--purple-soft)'
  }
]
const MARK_OPTIONS = [60, 70, 80, 100]
const REVIEW_CRITERIA = [
  {
    id: 'understanding',
    label: 'Understanding',
    icon: 'psychology',
    color: 'var(--accent-2)',
    bg: 'var(--accent-soft)'
  },
  {
    id: 'explanation',
    label: 'Explanation',
    icon: 'menu_book',
    color: 'var(--amber)',
    bg: 'var(--amber-soft)'
  },
  {
    id: 'accuracy',
    label: 'Accuracy',
    icon: 'fact_check',
    color: 'var(--green)',
    bg: 'var(--green-soft)'
  },
  {
    id: 'presentation',
    label: 'Presentation',
    icon: 'edit_note',
    color: 'var(--purple)',
    bg: 'var(--purple-soft)'
  },
  {
    id: 'depth',
    label: 'Depth',
    icon: 'layers',
    color: 'var(--sky)',
    bg: 'var(--sky-soft)'
  }
]
function markdownToHTML (text) {
  if (!text) return ''
  return text
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h2>$1</h2>')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^#### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^##### (.+)$/gm, '<h5>$1</h5>')
    .replace(/^###### (.+)$/gm, '<h6>$1</h6>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/^\* (.+)$/gm, '<li>$1</li>')
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, m => `<ul>${m}</ul>`)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
    .replace(/^---$/gm, '<hr>')
    .replace(/^(📌|💡|⚡|🔑)(.+)$/gm, '<div class="callout">$1$2</div>')
    .replace(/\n{2,}/g, '</p><p>')
    .replace(/^(?!<[hul]|<div|<hr)(.+)$/gm, '<p>$1</p>')
    .replace(/<p><\/p>/g, '')
}
/* Generation steps depend on which question types the chosen exam includes,
 * so e.g. a "Subjective Only" exam never shows an "MCQ" step. */
function genStepsFor (examType) {
  const hasMcq =
    examType === 'full' || examType === 'mcq_only' || examType === 'objective'
  const hasFill = examType === 'full' || examType === 'objective'
  const hasSubj = examType === 'full' || examType === 'subjective'

  const steps = [{ label: 'Reading notebook content' }]
  if (hasMcq) steps.push({ label: 'Crafting MCQ questions' })
  if (hasFill) steps.push({ label: 'Building fill-in-blank questions' })
  if (hasSubj) steps.push({ label: 'Creating subjective questions' })
  steps.push({ label: 'Assembling final paper' })
  return steps
}

/* ── Tiny shared helpers ─────────────────────────────────────────────────────── */
function SectionLabel ({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: 'var(--ink-3)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        marginBottom: 10
      }}
    >
      {children}
    </div>
  )
}
function SmallLabel ({ children, color }) {
  return (
    <div
      style={{
        fontSize: 10.5,
        fontWeight: 600,
        color: color || 'var(--ink-3)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: 5
      }}
    >
      {children}
    </div>
  )
}

/* ── Paper structure preview ─────────────────────────────────────────────────── */
function PaperBreakdown ({ examType, marks }) {
  let sections = []
  if (examType === 'full') {
    const a = Math.round(marks * 0.28),
      b = Math.round(marks * 0.14),
      c = marks - a - b
    sections = [
      { label: 'MCQ', marks: a, icon: 'checklist', color: 'var(--amber)' },
      {
        label: 'Fill-in-blanks',
        marks: b,
        icon: 'check_box',
        color: 'var(--sky)'
      },
      {
        label: 'Subjective',
        marks: c,
        icon: 'edit_note',
        color: 'var(--purple)'
      }
    ]
  } else if (examType === 'mcq_only') {
    sections = [
      { label: 'MCQ', marks, icon: 'checklist', color: 'var(--amber)' }
    ]
  } else if (examType === 'objective') {
    const a = Math.round(marks * 0.6)
    sections = [
      { label: 'MCQ', marks: a, icon: 'checklist', color: 'var(--amber)' },
      {
        label: 'Fill-in-blanks',
        marks: marks - a,
        icon: 'check_box',
        color: 'var(--sky)'
      }
    ]
  } else {
    sections = [
      { label: 'Subjective', marks, icon: 'edit_note', color: 'var(--purple)' }
    ]
  }
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
      {sections.map((s, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            minWidth: 110,
            padding: '10px 13px',
            borderRadius: 'var(--radius-lg)',
            background: 'var(--surface-2)',
            border: '1px solid var(--border)'
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: 6
            }}
          >
            <span
              className='material-symbols-rounded'
              style={{
                fontSize: 15,
                color: s.color,
                fontVariationSettings: "'FILL' 1"
              }}
            >
              {s.icon}
            </span>
            <span
              style={{ fontSize: 11, color: 'var(--ink-3)', fontWeight: 500 }}
            >
              {s.label}
            </span>
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>
            {s.marks}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>marks</div>
        </div>
      ))}
    </div>
  )
}

/* ── Setup view ──────────────────────────────────────────────────────────────── */
function SetupView ({ notebooks, onStart, onViewHistory, isGenerating, error }) {
  const [examType, setExamType] = useState('full')
  const [marks, setMarks] = useState(70)
  const [notebookId, setNotebookId] = useState(notebooks[0]?.id || '')

  return (
    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: '28px 32px',
        maxWidth: 760,
        margin: '0 auto',
        width: '100%'
      }}
    >
      <div
        style={{
          marginBottom: 28,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16
        }}
      >
        <div>
          <h2
            style={{
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: -0.5,
              marginBottom: 6
            }}
          >
            Take an Exam
          </h2>
          <p style={{ color: 'var(--ink-3)', fontSize: 13.5, lineHeight: 1.6 }}>
            AI generates a real exam paper from your notebook — every question
            is based on what you studied.
          </p>
        </div>
        <button
          onClick={() => notebookId && onViewHistory?.(notebookId)}
          disabled={!notebookId}
          className='btn btn-surface'
          style={{ gap: 7, flexShrink: 0, opacity: notebookId ? 1 : 0.5 }}
          title='View your previous exam attempts'
        >
          <span className='material-symbols-rounded' style={{ fontSize: 17 }}>
            history
          </span>
          Past Exams
        </button>
      </div>

      {error && (
        <div
          style={{
            background: 'var(--rose-soft)',
            border: '1px solid rgba(244,63,94,0.25)',
            borderRadius: 'var(--radius-lg)',
            padding: '10px 14px',
            marginBottom: 18,
            fontSize: 13,
            color: 'var(--rose)',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <span
            className='material-symbols-rounded'
            style={{ fontSize: 16, fontVariationSettings: "'FILL' 1" }}
          >
            error
          </span>
          {error}
        </div>
      )}

      <SectionLabel>Exam Format</SectionLabel>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
          gap: 10,
          marginBottom: 24
        }}
      >
        {EXAM_TYPES.map(et => (
          <button
            key={et.id}
            onClick={() => setExamType(et.id)}
            style={{
              background: 'var(--surface)',
              border: '1.5px solid',
              borderColor: examType === et.id ? et.color : 'var(--border)',
              borderRadius: 'var(--radius-xl)',
              padding: '14px 16px',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all var(--transition)',
              boxShadow: examType === et.id ? `0 0 0 3px ${et.bg}` : 'none'
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                marginBottom: 10,
                background: examType === et.id ? et.bg : 'var(--surface-3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <span
                className='material-symbols-rounded'
                style={{
                  fontSize: 19,
                  color: examType === et.id ? et.color : 'var(--ink-3)',
                  fontVariationSettings: "'FILL' 1"
                }}
              >
                {et.icon}
              </span>
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: examType === et.id ? et.color : 'var(--ink)',
                marginBottom: 3
              }}
            >
              {et.label}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
              {et.desc}
            </div>
          </button>
        ))}
      </div>

      <SectionLabel>Total Marks</SectionLabel>
      <div
        style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}
      >
        {MARK_OPTIONS.map(m => (
          <button
            key={m}
            onClick={() => setMarks(m)}
            style={{
              padding: '8px 20px',
              borderRadius: 'var(--radius-full)',
              border: '1.5px solid',
              borderColor: marks === m ? 'var(--accent)' : 'var(--border-2)',
              background: marks === m ? 'var(--accent-soft)' : 'var(--surface)',
              color: marks === m ? 'var(--accent-2)' : 'var(--ink-2)',
              fontWeight: marks === m ? 600 : 500,
              fontSize: 13.5,
              cursor: 'pointer',
              transition: 'all var(--transition)'
            }}
          >
            {m} Marks
          </button>
        ))}
      </div>

      <SectionLabel>Notebook</SectionLabel>
      <select
        value={notebookId}
        onChange={e => setNotebookId(e.target.value)}
        style={{ maxWidth: 420, marginBottom: 24 }}
      >
        {notebooks.map(nb => (
          <option key={nb.id} value={nb.id}>
            {nb.title}
          </option>
        ))}
      </select>

      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-xl)',
          padding: '16px 20px',
          marginBottom: 28
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--ink-3)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: 12
          }}
        >
          Expected Paper Structure
        </div>
        <PaperBreakdown examType={examType} marks={marks} />
      </div>

      <button
        onClick={() => onStart({ examType, marks, notebookId })}
        disabled={isGenerating || !notebookId}
        className='btn btn-primary btn-lg'
        style={{
          fontSize: 15,
          padding: '13px 28px',
          boxShadow: '0 4px 16px var(--accent-glow)',
          opacity: isGenerating || !notebookId ? 0.65 : 1
        }}
      >
        {isGenerating ? (
          <>
            <div
              className='spinner spinner-sm'
              style={{
                borderTopColor: 'rgba(255,255,255,0.4)',
                borderColor: 'rgba(255,255,255,0.15)'
              }}
            />{' '}
            Generating Paper…
          </>
        ) : (
          <>
            <span
              className='material-symbols-rounded'
              style={{ fontSize: 19, fontVariationSettings: "'FILL' 1" }}
            >
              auto_awesome
            </span>{' '}
            Generate Exam Paper
          </>
        )}
      </button>
    </div>
  )
}

/* ── Generating view ──────────────────────────────────────────────────────────── */
function GeneratingView ({ steps, currentStep }) {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 20,
        padding: '48px 32px'
      }}
    >
      <div style={{ position: 'relative', width: 64, height: 64 }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            border: '3px solid var(--border-2)',
            borderTopColor: 'var(--accent)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <span
            className='material-symbols-rounded'
            style={{
              fontSize: 28,
              color: 'var(--accent-2)',
              fontVariationSettings: "'FILL' 1"
            }}
          >
            auto_awesome
          </span>
        </div>
      </div>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>
          Generating your exam paper
        </div>
        <div className='ai-indicator' style={{ display: 'inline-flex' }}>
          <span
            className='material-symbols-rounded'
            style={{ fontSize: 14, fontVariationSettings: "'FILL' 1" }}
          >
            smart_toy
          </span>
          AI is reading your notebook
          <div className='dots'>
            <div className='dot' />
            <div className='dot' />
            <div className='dot' />
          </div>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          width: '100%',
          maxWidth: 360
        }}
      >
        {steps.map((step, i) => {
          const done = i < currentStep,
            active = i === currentStep
          return (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 14px',
                borderRadius: 'var(--radius-lg)',
                background: done
                  ? 'var(--green-soft)'
                  : active
                  ? 'var(--accent-soft)'
                  : 'var(--surface-2)',
                border: '1px solid',
                borderColor: done
                  ? 'rgba(16,185,129,0.2)'
                  : active
                  ? 'rgba(99,102,241,0.2)'
                  : 'var(--border)',
                opacity: i > currentStep ? 0.4 : 1,
                transition: 'all 0.3s ease'
              }}
            >
              {done ? (
                <span
                  className='material-symbols-rounded'
                  style={{
                    fontSize: 16,
                    color: 'var(--green)',
                    flexShrink: 0,
                    fontVariationSettings: "'FILL' 1"
                  }}
                >
                  check_circle
                </span>
              ) : active ? (
                <div
                  className='spinner spinner-sm'
                  style={{ flexShrink: 0, borderTopColor: 'var(--accent)' }}
                />
              ) : (
                <div
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    border: '2px solid var(--border-2)',
                    flexShrink: 0
                  }}
                />
              )}
              <span
                style={{
                  fontSize: 12.5,
                  fontWeight: active ? 600 : 500,
                  color: done
                    ? 'var(--green)'
                    : active
                    ? 'var(--accent-2)'
                    : 'var(--ink-3)'
                }}
              >
                {step.label}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ── Exam paper view ──────────────────────────────────────────────────────────── */
function ExamPaperView ({
  paper,
  answers,
  setAnswers,
  onSubmit,
  examType,
  marks,
  submitError
}) {
  const [timerSec, setTimerSec] = useState(marks * 60)
  useEffect(() => {
    const id = setInterval(() => setTimerSec(t => Math.max(0, t - 1)), 1000)
    return () => clearInterval(id)
  }, [])

  const fmt = s =>
    `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(
      2,
      '0'
    )}`
  const sections = paper.sections || []
  const totalQs = sections.reduce((a, s) => a + (s.questions?.length || 0), 0)
  const answered = Object.values(answers).filter(a => a !== '').length
  const setAns = (qId, val) => setAnswers(prev => ({ ...prev, [qId]: val }))

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 24px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {paper.notebook_title} · {marks} Marks
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
            {totalQs} questions ·{' '}
            {EXAM_TYPES.find(e => e.id === examType)?.label}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            <span style={{ color: 'var(--ink-2)', fontWeight: 600 }}>
              {answered}
            </span>
            /{totalQs} answered
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 12px',
              borderRadius: 'var(--radius-full)',
              background:
                timerSec < 300 ? 'var(--rose-soft)' : 'var(--amber-soft)',
              border: `1px solid ${
                timerSec < 300 ? 'rgba(244,63,94,0.2)' : 'rgba(245,158,11,0.2)'
              }`,
              color: timerSec < 300 ? 'var(--rose)' : 'var(--amber)',
              fontWeight: 600,
              fontSize: 13
            }}
          >
            <span className='material-symbols-rounded' style={{ fontSize: 15 }}>
              timer
            </span>
            {fmt(timerSec)}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div
        className='progress-track'
        style={{ height: 3, borderRadius: 0, flexShrink: 0 }}
      >
        <div
          className='progress-fill'
          style={{
            width: `${(answered / Math.max(totalQs, 1)) * 100}%`,
            borderRadius: 0
          }}
        />
      </div>

      {/* Questions scroll */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>
        {sections.map((section, si) => (
          <div key={si} style={{ marginBottom: 32 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 16,
                paddingBottom: 10,
                borderBottom: '1px solid var(--border)'
              }}
            >
              <span
                style={{
                  padding: '3px 10px',
                  borderRadius: 'var(--radius-full)',
                  fontSize: 11,
                  fontWeight: 600,
                  background: section.color_bg,
                  color: section.color
                }}
              >
                {section.label}
              </span>
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                {section.title}
              </span>
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: 11.5,
                  color: 'var(--ink-3)'
                }}
              >
                {section.total_marks} marks
              </span>
            </div>
            {(section.questions || []).map(q => (
              <QuestionBlock
                key={q.id}
                q={q}
                answer={answers[q.id] ?? ''}
                setAnswer={val => setAns(q.id, val)}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '12px 24px',
          borderTop: '1px solid var(--border)',
          background: 'var(--surface)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexShrink: 0
        }}
      >
        {submitError && (
          <div style={{ fontSize: 12, color: 'var(--rose)' }}>
            <span
              className='material-symbols-rounded'
              style={{ fontSize: 14, verticalAlign: 'middle' }}
            >
              error
            </span>{' '}
            {submitError}
          </div>
        )}
        <div style={{ flex: 1, fontSize: 12, color: 'var(--ink-3)' }}>
          {answered < totalQs ? (
            `${totalQs - answered} questions unanswered`
          ) : (
            <span style={{ color: 'var(--green)', fontWeight: 600 }}>
              ✓ All questions answered
            </span>
          )}
        </div>
        <button
          onClick={onSubmit}
          className='btn btn-success btn-lg'
          style={{ gap: 8 }}
        >
          <span
            className='material-symbols-rounded'
            style={{ fontSize: 18, fontVariationSettings: "'FILL' 1" }}
          >
            check_circle
          </span>
          Submit &amp; Get AI Review
        </button>
      </div>
    </div>
  )
}

/* ── Individual question block ───────────────────────────────────────────────── */
function QuestionBlock ({ q, answer, setAnswer }) {
  const hasAnswer = answer !== '' && answer !== undefined
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid',
        borderRadius: 'var(--radius-xl)',
        padding: '16px 18px',
        marginBottom: 10,
        transition: 'border-color var(--transition)',
        borderColor: hasAnswer ? 'rgba(99,102,241,0.22)' : 'var(--border)'
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          marginBottom: 10
        }}
      >
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 600,
            color: 'var(--ink-3)',
            background: 'var(--surface-3)',
            padding: '2px 8px',
            borderRadius: 'var(--radius-full)',
            flexShrink: 0,
            marginTop: 2
          }}
        >
          Q{q.num} · {q.marks} {q.marks === 1 ? 'mark' : 'marks'}
        </span>
        {hasAnswer && (
          <span
            className='material-symbols-rounded'
            style={{
              fontSize: 16,
              color: 'var(--green)',
              fontVariationSettings: "'FILL' 1",
              marginLeft: 'auto',
              flexShrink: 0
            }}
          >
            check_circle
          </span>
        )}
      </div>
      <div
        style={{
          fontSize: 13.5,
          lineHeight: 1.65,
          color: 'var(--ink)',
          marginBottom: 12,
          fontWeight: 500
        }}
      >
        {q.text}
      </div>

      {q.type === 'mcq' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {(q.options || []).map((opt, oi) => {
            const label = String.fromCharCode(65 + oi)
            const selected = answer === label
            return (
              <div
                key={oi}
                onClick={() => setAnswer(label)}
                className='quiz-option'
                style={{
                  borderColor: selected ? 'var(--accent)' : 'var(--border-2)',
                  background: selected
                    ? 'var(--accent-soft)'
                    : 'var(--surface)',
                  color: selected ? 'var(--accent-2)' : 'var(--ink)',
                  cursor: 'pointer'
                }}
              >
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    border: `2px solid ${
                      selected ? 'var(--accent)' : 'var(--border-2)'
                    }`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 10,
                    fontWeight: 700,
                    flexShrink: 0,
                    background: selected ? 'var(--accent)' : 'transparent',
                    color: selected ? 'white' : 'var(--ink-3)'
                  }}
                >
                  {label}
                </div>
                {opt}
              </div>
            )
          })}
        </div>
      )}
      {q.type === 'fill' && (
        <input
          className='fill-blank'
          placeholder='Type your answer…'
          value={answer}
          onChange={e => setAnswer(e.target.value)}
        />
      )}
      {q.type === 'subjective' && (
        <textarea
          className='subj-textarea'
          placeholder='Write your answer here…'
          rows={q.marks >= 10 ? 8 : q.marks >= 5 ? 5 : 3}
          value={answer}
          onChange={e => setAnswer(e.target.value)}
        />
      )}
    </div>
  )
}

/* ── Reviewing loading view ──────────────────────────────────────────────────── */
function ReviewingView () {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 20,
        padding: '48px 32px'
      }}
    >
      <div style={{ position: 'relative', width: 64, height: 64 }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            border: '3px solid var(--border-2)',
            borderTopColor: 'var(--green)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <span
            className='material-symbols-rounded'
            style={{
              fontSize: 28,
              color: 'var(--green)',
              fontVariationSettings: "'FILL' 1"
            }}
          >
            rate_review
          </span>
        </div>
      </div>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>
          AI is reviewing your answers
        </div>
        <div
          style={{
            fontSize: 13,
            color: 'var(--ink-3)',
            marginBottom: 14,
            lineHeight: 1.6
          }}
        >
          Every answer is evaluated for understanding, accuracy,
          <br />
          explanation quality, presentation and depth.
        </div>
        <div className='ai-indicator' style={{ display: 'inline-flex' }}>
          <span
            className='material-symbols-rounded'
            style={{ fontSize: 14, fontVariationSettings: "'FILL' 1" }}
          >
            smart_toy
          </span>
          Evaluating all criteria
          <div className='dots'>
            <div className='dot' />
            <div className='dot' />
            <div className='dot' />
          </div>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          justifyContent: 'center',
          maxWidth: 360
        }}
      >
        {REVIEW_CRITERIA.map((c, i) => (
          <div
            key={c.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '5px 12px',
              borderRadius: 'var(--radius-full)',
              background: c.bg,
              fontSize: 11.5,
              fontWeight: 500,
              color: c.color,
              animation: `fadeIn 0.3s ease ${i * 0.12}s both`
            }}
          >
            <span
              className='material-symbols-rounded'
              style={{ fontSize: 13, fontVariationSettings: "'FILL' 1" }}
            >
              {c.icon}
            </span>
            {c.label}
          </div>
        ))}
      </div>
    </div>
  )
}

/* ── "Discuss Mistakes with AI" side chat ────────────────────────────────────── */
/* Builds a compact, readable summary of the graded exam so the tutor can answer
 * questions about specific mistakes. Wrong answers are prioritised. */
function buildMistakeContext (review) {
  const lines = []
  lines.push(
    `Exam result: ${review.score}/${review.total} (${review.percentage}%), grade "${review.grade}".`
  )
  if (review.summary) lines.push(`Overall examiner feedback: ${review.summary}`)
  const qs = review.questions || []
  const wrong = qs.filter(q => q.status !== 'correct')
  const focus = (wrong.length ? wrong : qs).slice(0, 10)
  focus.forEach(q => {
    lines.push(
      `\nQ${q.num} (${q.marks} mark${q.marks === 1 ? '' : 's'}, scored ${
        q.scored
      }/${q.marks}): ${q.question}\n` +
        `Student's answer: ${q.student_answer || 'Not answered'}\n` +
        `Correct / model answer: ${q.model_answer || '—'}` +
        (q.remark ? `\nExaminer feedback: ${q.remark}` : '')
    )
  })
  return lines.join('\n')
}

function MistakesChat ({ notebookId, review, onClose }) {
  const context = useMemo(() => buildMistakeContext(review), [review])
  const wrongQs = useMemo(
    () => (review.questions || []).filter(q => q.status !== 'correct'),
    [review]
  )

  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content:
        "I've got your graded exam in front of me — every question, the answer you wrote, and the correct answer. Ask me about any mistake and I'll explain what went wrong and how to fix it."
    }
  ])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const scrollRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth'
    })
  }, [messages, busy])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const send = async text => {
    const q = (text ?? input).trim()
    if (!q || busy) return
    // History = prior turns (backend appends the new question itself).
    const priorHistory = messages
      .slice(-8)
      .map(m => ({ role: m.role, content: m.content }))
    setInput('')
    setMessages(m => [...m, { role: 'user', content: q }])
    setBusy(true)
    try {
      const res = await api.chatWithNotebook(
        notebookId,
        q,
        priorHistory,
        'student',
        false,
        context
      )
      setMessages(m => [
        ...m,
        { role: 'assistant', content: res.response || '…' }
      ])
    } catch (e) {
      setMessages(m => [
        ...m,
        {
          role: 'assistant',
          content: `Sorry, I couldn't respond right now: ${e.message || e}`
        }
      ])
    } finally {
      setBusy(false)
    }
  }

  const suggestions = wrongQs
    .slice(0, 3)
    .map(q => `Explain what I got wrong on Q${q.num}`)

  return (
    <div
      style={{
        width: 400,
        maxWidth: '45%',
        flexShrink: 0,
        borderLeft: '1px solid var(--border)',
        background: 'var(--surface)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
      className='animate-fadeIn'
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexShrink: 0
        }}
      >
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 9,
            background: 'var(--accent-soft)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <span
            className='material-symbols-rounded'
            style={{
              fontSize: 17,
              color: 'var(--accent-2)',
              fontVariationSettings: "'FILL' 1"
            }}
          >
            smart_toy
          </span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Discuss Mistakes</div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            AI tutor · knows your exam
          </div>
        </div>
        <button
          onClick={onClose}
          className='btn btn-ghost btn-sm'
          style={{ padding: 6 }}
          title='Close chat'
        >
          <span className='material-symbols-rounded' style={{ fontSize: 18 }}>
            close
          </span>
        </button>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '14px 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12
        }}
      >
        {messages.map((m, i) => {
          return (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start'
              }}
            >
              <div
                style={{
                  maxWidth: '88%',
                  padding: '9px 12px',
                  borderRadius: 14,
                  fontSize: 13,
                  lineHeight: 1.6,
                  wordBreak: 'break-word',
                  background:
                    m.role === 'user' ? 'var(--accent)' : 'var(--surface-2)',
                  color: m.role === 'user' ? '#fff' : 'var(--ink)',
                  border:
                    m.role === 'user' ? 'none' : '1px solid var(--border)',
                  borderBottomRightRadius: m.role === 'user' ? 4 : 14,
                  borderBottomLeftRadius: m.role === 'user' ? 14 : 4
                }}
              >
                <div
                  className={
                    m.role === 'user'
                      ? 'md-content md-content-on-accent'
                      : 'md-content'
                  }
                  dangerouslySetInnerHTML={{
                    __html: markdownToHTML(m.content)
                  }}
                />
              </div>
            </div>
          )
        })}
        {busy && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div
              style={{
                padding: '9px 12px',
                borderRadius: 14,
                background: 'var(--surface-2)',
                border: '1px solid var(--border)'
              }}
            >
              <div style={{ display: 'flex', gap: 5, padding: '3px 0' }}>
                <div className='typing-dot' />
                <div className='typing-dot' />
                <div className='typing-dot' />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Suggestions */}
      {messages.length <= 1 && suggestions.length > 0 && (
        <div
          style={{
            padding: '0 14px 10px',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            flexShrink: 0
          }}
        >
          {suggestions.map((s, i) => (
            <button
              key={i}
              onClick={() => send(s)}
              disabled={busy}
              style={{
                textAlign: 'left',
                padding: '7px 11px',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                background: 'var(--surface-2)',
                color: 'var(--ink-2)',
                cursor: busy ? 'default' : 'pointer',
                fontSize: 12,
                fontFamily: 'var(--font-sans)'
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div
        style={{
          padding: '10px 12px',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          gap: 8,
          flexShrink: 0
        }}
      >
        <textarea
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              send()
            }
          }}
          placeholder='Ask about a mistake…'
          rows={1}
          style={{
            flex: 1,
            resize: 'none',
            fontSize: 13,
            fontFamily: 'var(--font-sans)',
            maxHeight: 100
          }}
        />
        <button
          onClick={() => send()}
          disabled={busy || !input.trim()}
          className='btn btn-primary'
          style={{
            padding: '8px 12px',
            alignSelf: 'flex-end',
            opacity: busy || !input.trim() ? 0.6 : 1
          }}
        >
          <span
            className='material-symbols-rounded'
            style={{ fontSize: 18, fontVariationSettings: "'FILL' 1" }}
          >
            send
          </span>
        </button>
      </div>
    </div>
  )
}

/* ── Full review results view ────────────────────────────────────────────────── */
function ReviewView ({
  review,
  notebookId,
  onRetake,
  onChat,
  onBack,
  backLabel
}) {
  const pct = review.percentage || 0
  const grade = review.grade || 'Fair'
  const gradeColor =
    pct >= 90
      ? 'var(--green)'
      : pct >= 75
      ? 'var(--sky)'
      : pct >= 60
      ? 'var(--amber)'
      : 'var(--rose)'

  // "Discuss Mistakes with AI" opens a chat panel beside the review, pre-loaded
  // with this exam's questions / answers so the tutor can clarify mistakes.
  const [chatOpen, setChatOpen] = useState(false)
  const canChat = !!notebookId

  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px 28px',
          minWidth: 0
        }}
        className='animate-fadeIn'
      >
        {/* Score banner */}
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-2xl)',
            padding: '22px 24px',
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 20
          }}
        >
          <div
            style={{
              width: 76,
              height: 76,
              borderRadius: '50%',
              flexShrink: 0,
              border: `3px solid ${gradeColor}`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 0 20px ${gradeColor}30`
            }}
          >
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: gradeColor,
                lineHeight: 1
              }}
            >
              {review.score}
            </div>
            <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 2 }}>
              /{review.total}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 5
              }}
            >
              <h3 style={{ fontSize: 17, fontWeight: 600 }}>{grade}</h3>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: gradeColor,
                  background: `${gradeColor}18`,
                  padding: '2px 9px',
                  borderRadius: 'var(--radius-full)'
                }}
              >
                {pct}%
              </span>
            </div>
            <p
              style={{
                fontSize: 13,
                color: 'var(--ink-3)',
                marginBottom: 12,
                lineHeight: 1.6
              }}
            >
              {review.summary}
            </p>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
              {REVIEW_CRITERIA.map(c => {
                const val = review.criteria?.[c.id]
                if (!val || val === 'N/A') return null
                return (
                  <div
                    key={c.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '3px 10px',
                      borderRadius: 'var(--radius-full)',
                      background: c.bg,
                      color: c.color,
                      fontSize: 11,
                      fontWeight: 600
                    }}
                  >
                    <span
                      className='material-symbols-rounded'
                      style={{
                        fontSize: 12,
                        fontVariationSettings: "'FILL' 1"
                      }}
                    >
                      {c.icon}
                    </span>
                    {c.label}: {val}
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Strengths & Improvements */}
        {(review.strengths?.length > 0 || review.improvements?.length > 0) && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 12,
              marginBottom: 20
            }}
          >
            {review.strengths?.length > 0 && (
              <div
                style={{
                  background: 'var(--surface)',
                  border: '1px solid rgba(16,185,129,0.2)',
                  borderRadius: 'var(--radius-xl)',
                  padding: '14px 16px'
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--green)',
                    marginBottom: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  <span
                    className='material-symbols-rounded'
                    style={{ fontSize: 15, fontVariationSettings: "'FILL' 1" }}
                  >
                    verified
                  </span>{' '}
                  Strengths
                </div>
                {review.strengths.map((s, i) => (
                  <div
                    key={i}
                    style={{
                      fontSize: 12,
                      color: 'var(--ink-2)',
                      padding: '4px 0',
                      borderBottom:
                        i < review.strengths.length - 1
                          ? '1px solid var(--border)'
                          : 'none'
                    }}
                  >
                    {s}
                  </div>
                ))}
              </div>
            )}
            {review.improvements?.length > 0 && (
              <div
                style={{
                  background: 'var(--surface)',
                  border: '1px solid rgba(245,158,11,0.2)',
                  borderRadius: 'var(--radius-xl)',
                  padding: '14px 16px'
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--amber)',
                    marginBottom: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  <span
                    className='material-symbols-rounded'
                    style={{ fontSize: 15, fontVariationSettings: "'FILL' 1" }}
                  >
                    warning
                  </span>{' '}
                  Areas to Improve
                </div>
                {review.improvements.map((s, i) => (
                  <div
                    key={i}
                    style={{
                      fontSize: 12,
                      color: 'var(--ink-2)',
                      padding: '4px 0',
                      borderBottom:
                        i < review.improvements.length - 1
                          ? '1px solid var(--border)'
                          : 'none'
                    }}
                  >
                    {s}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Per-question detailed review */}
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--ink-3)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginBottom: 10
          }}
        >
          Detailed Review
        </div>
        {(review.questions || []).map((qr, i) => (
          <ReviewQuestionBlock key={i} qr={qr} />
        ))}

        <div
          style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}
        >
          {onBack ? (
            <button
              onClick={onBack}
              className='btn btn-primary'
              style={{ gap: 7 }}
            >
              <span
                className='material-symbols-rounded'
                style={{ fontSize: 16 }}
              >
                arrow_back
              </span>
              {backLabel || 'Back to Past Exams'}
            </button>
          ) : (
            <button
              onClick={onRetake}
              className='btn btn-primary'
              style={{ gap: 7 }}
            >
              <span
                className='material-symbols-rounded'
                style={{ fontSize: 16 }}
              >
                refresh
              </span>
              Take Another Exam
            </button>
          )}
          {canChat ? (
            <button
              onClick={() => setChatOpen(o => !o)}
              className={`btn ${chatOpen ? 'btn-primary' : 'btn-surface'}`}
              style={{ gap: 7 }}
            >
              <span
                className='material-symbols-rounded'
                style={{ fontSize: 16, fontVariationSettings: "'FILL' 1" }}
              >
                chat
              </span>
              {chatOpen ? 'Hide AI Chat' : 'Discuss Mistakes with AI'}
            </button>
          ) : (
            onChat && (
              <button
                onClick={onChat}
                className='btn btn-surface'
                style={{ gap: 7 }}
              >
                <span
                  className='material-symbols-rounded'
                  style={{ fontSize: 16, fontVariationSettings: "'FILL' 1" }}
                >
                  chat
                </span>
                Discuss Mistakes with AI
              </button>
            )
          )}
        </div>
      </div>

      {chatOpen && canChat && (
        <MistakesChat
          notebookId={notebookId}
          review={review}
          onClose={() => setChatOpen(false)}
        />
      )}
    </div>
  )
}

/* ── Per-question review accordion ──────────────────────────────────────────── */
function ReviewQuestionBlock ({ qr }) {
  const [expanded, setExpanded] = useState(false)
  const isCorrect = qr.status === 'correct'
  const isPartial = qr.status === 'partial'

  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid',
        borderRadius: 'var(--radius-xl)',
        marginBottom: 10,
        overflow: 'hidden',
        borderColor: isCorrect
          ? 'rgba(16,185,129,0.2)'
          : isPartial
          ? 'rgba(245,158,11,0.2)'
          : 'rgba(244,63,94,0.15)'
      }}
    >
      {/* Collapsed header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 16px',
          cursor: 'pointer'
        }}
        onClick={() => setExpanded(e => !e)}
      >
        <span
          className='material-symbols-rounded'
          style={{
            fontSize: 18,
            flexShrink: 0,
            fontVariationSettings: "'FILL' 1",
            color: isCorrect
              ? 'var(--green)'
              : isPartial
              ? 'var(--amber)'
              : 'var(--rose)'
          }}
        >
          {isCorrect ? 'check_circle' : isPartial ? 'change_history' : 'cancel'}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--ink-3)',
              marginBottom: 2
            }}
          >
            Q{qr.num} · {qr.marks} marks
          </div>
          <div
            style={{
              fontSize: 13,
              color: 'var(--ink)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {qr.question}
          </div>
        </div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            flexShrink: 0,
            color: isCorrect
              ? 'var(--green)'
              : isPartial
              ? 'var(--amber)'
              : 'var(--rose)'
          }}
        >
          {qr.scored}/{qr.marks}
        </div>
        <span
          className='material-symbols-rounded'
          style={{
            fontSize: 18,
            color: 'var(--ink-3)',
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s ease'
          }}
        >
          expand_more
        </span>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div
          style={{
            padding: '0 16px 16px',
            borderTop: '1px solid var(--border)'
          }}
          className='animate-fadeIn'
        >
          <div
            style={{
              marginTop: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 10
            }}
          >
            {/* Student answer */}
            <div>
              <SmallLabel>Your Answer</SmallLabel>
              <div className='student-answer-block'>
                {qr.student_answer || (
                  <em style={{ color: 'var(--ink-3)' }}>Not answered</em>
                )}
              </div>
            </div>

            {/* Model answer */}
            <div>
              <SmallLabel color='var(--green)'>✓ Model Answer</SmallLabel>
              <div className='ideal-answer-block'>{qr.model_answer}</div>
            </div>

            {/* AI feedback */}
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginBottom: 5
                }}
              >
                <span
                  className='material-symbols-rounded'
                  style={{
                    fontSize: 14,
                    color: 'var(--accent-2)',
                    fontVariationSettings: "'FILL' 1"
                  }}
                >
                  auto_awesome
                </span>
                <SmallLabel color='var(--accent-2)'>AI Feedback</SmallLabel>
              </div>
              <div className='remark-block'>{qr.remark}</div>
            </div>

            {/* How to improve */}
            {qr.improvements?.length > 0 && (
              <div>
                <SmallLabel color='var(--amber)'>How to Improve</SmallLabel>
                <ul style={{ paddingLeft: 16, margin: 0 }}>
                  {qr.improvements.map((imp, i) => (
                    <li
                      key={i}
                      style={{
                        fontSize: 12,
                        color: 'var(--ink-2)',
                        marginBottom: 4,
                        lineHeight: 1.6
                      }}
                    >
                      {imp}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Per-criteria scores */}
            {qr.criteria && Object.keys(qr.criteria).length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {Object.entries(qr.criteria).map(([key, val]) => {
                  const c = REVIEW_CRITERIA.find(r => r.id === key)
                  if (!c) return null
                  return (
                    <div
                      key={key}
                      style={{
                        padding: '3px 9px',
                        borderRadius: 'var(--radius-md)',
                        background: c.bg,
                        color: c.color,
                        fontSize: 11,
                        fontWeight: 600
                      }}
                    >
                      {c.label}: {val}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/* ── Exam history view ──────────────────────────────────────────────────────── */
function gradeColorFor (pct) {
  return pct >= 90
    ? 'var(--green)'
    : pct >= 75
    ? 'var(--sky)'
    : pct >= 60
    ? 'var(--amber)'
    : 'var(--rose)'
}

function formatAttemptDate (ts) {
  try {
    return new Intl.DateTimeFormat('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(ts))
  } catch {
    return '—'
  }
}

function HistoryView ({ notebookId, notebookTitle, onBack, onNewExam }) {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [selected, setSelected] = useState(null) // an attempt being reviewed in detail

  useEffect(() => {
    let alive = true
    api
      .getExamHistory(notebookId)
      .then(res => {
        if (alive) setData(res)
      })
      .catch(err => {
        if (alive) setError(err.message || 'Could not load exam history.')
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [notebookId])

  // Newest attempts first.
  const attempts = [...(data?.attempts || [])].reverse()
  const bestScore = data?.best_score

  // Drill-in: show the full question-by-question review for one past attempt.
  if (selected?.review) {
    return (
      <ReviewView
        review={selected.review}
        notebookId={notebookId}
        onBack={() => setSelected(null)}
        backLabel='Back to Past Exams'
      />
    )
  }

  return (
    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: '28px 32px',
        maxWidth: 760,
        margin: '0 auto',
        width: '100%'
      }}
      className='animate-fadeIn'
    >
      <div style={{ marginBottom: 22 }}>
        <h2
          style={{
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: -0.5,
            marginBottom: 6
          }}
        >
          Past Exams
        </h2>
        <p style={{ color: 'var(--ink-3)', fontSize: 13.5, lineHeight: 1.6 }}>
          Your previous exam attempts for{' '}
          <strong style={{ color: 'var(--ink-2)' }}>{notebookTitle}</strong>.
        </p>
      </div>

      {/* Loading */}
      {loading && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 14,
            padding: '56px 0'
          }}
        >
          <div
            className='spinner'
            style={{ borderTopColor: 'var(--accent)' }}
          />
          <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
            Loading your exam history…
          </div>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div
          style={{
            background: 'var(--rose-soft)',
            border: '1px solid rgba(244,63,94,0.25)',
            borderRadius: 'var(--radius-lg)',
            padding: '12px 16px',
            fontSize: 13,
            color: 'var(--rose)',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <span
            className='material-symbols-rounded'
            style={{ fontSize: 16, fontVariationSettings: "'FILL' 1" }}
          >
            error
          </span>
          {error}
        </div>
      )}

      {/* Empty */}
      {!loading && !error && attempts.length === 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            padding: '48px 24px',
            textAlign: 'center'
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'var(--surface-2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid var(--border)'
            }}
          >
            <span
              className='material-symbols-rounded'
              style={{ fontSize: 30, color: 'var(--ink-3)' }}
            >
              history
            </span>
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
              No exams yet
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--ink-3)',
                maxWidth: 320,
                lineHeight: 1.6
              }}
            >
              You haven't completed any exams for this notebook. Take one and
              it'll show up here.
            </div>
          </div>
          <button
            onClick={onNewExam}
            className='btn btn-primary'
            style={{ gap: 7 }}
          >
            <span
              className='material-symbols-rounded'
              style={{ fontSize: 17, fontVariationSettings: "'FILL' 1" }}
            >
              auto_awesome
            </span>
            Take an Exam
          </button>
        </div>
      )}

      {/* Attempts list */}
      {!loading && !error && attempts.length > 0 && (
        <>
          {/* Summary strip */}
          <div
            style={{
              display: 'flex',
              gap: 12,
              marginBottom: 20,
              flexWrap: 'wrap'
            }}
          >
            <div
              style={{
                flex: 1,
                minWidth: 140,
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)',
                padding: '14px 16px'
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--ink-3)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: 6
                }}
              >
                Attempts
              </div>
              <div
                style={{ fontSize: 24, fontWeight: 700, color: 'var(--ink)' }}
              >
                {data.count}
              </div>
            </div>
            <div
              style={{
                flex: 1,
                minWidth: 140,
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)',
                padding: '14px 16px'
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--ink-3)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: 6
                }}
              >
                Best Score
              </div>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  color:
                    bestScore != null ? gradeColorFor(bestScore) : 'var(--ink)'
                }}
              >
                {bestScore != null ? `${bestScore}%` : '—'}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {attempts.map((a, i) => {
              const pct =
                a.pct ?? (a.total ? Math.round((a.score / a.total) * 100) : 0)
              const color = gradeColorFor(pct)
              const typeLabel =
                EXAM_TYPES.find(e => e.id === a.exam_type)?.label ||
                (a.exam_type || 'Exam').replace('_', ' ')
              const reviewable = !!a.review?.questions?.length
              return (
                <div
                  key={i}
                  onClick={() => reviewable && setSelected(a)}
                  title={
                    reviewable
                      ? 'View your answers and the correct answers'
                      : "Detailed review isn't available for this older attempt"
                  }
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-xl)',
                    padding: '14px 16px',
                    cursor: reviewable ? 'pointer' : 'default',
                    transition:
                      'border-color var(--transition), background var(--transition)'
                  }}
                  onMouseEnter={e => {
                    if (reviewable) {
                      e.currentTarget.style.borderColor = color
                      e.currentTarget.style.background = 'var(--surface-2)'
                    }
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = 'var(--border)'
                    e.currentTarget.style.background = 'var(--surface)'
                  }}
                >
                  <div
                    style={{
                      width: 46,
                      height: 46,
                      borderRadius: '50%',
                      flexShrink: 0,
                      border: `2.5px solid ${color}`,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color,
                        lineHeight: 1
                      }}
                    >
                      {pct}%
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginBottom: 3
                      }}
                    >
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: 'var(--ink)'
                        }}
                      >
                        {a.grade || '—'}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color,
                          background: `${color}18`,
                          padding: '1px 8px',
                          borderRadius: 'var(--radius-full)'
                        }}
                      >
                        {a.score}/{a.total}
                      </span>
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                      {typeLabel} · {formatAttemptDate(a.ts)}
                    </div>
                  </div>
                  {reviewable ? (
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3,
                        fontSize: 12,
                        fontWeight: 600,
                        color: 'var(--accent-2)',
                        flexShrink: 0
                      }}
                    >
                      Review
                      <span
                        className='material-symbols-rounded'
                        style={{ fontSize: 18 }}
                      >
                        chevron_right
                      </span>
                    </span>
                  ) : (
                    <span
                      className='material-symbols-rounded'
                      style={{
                        fontSize: 20,
                        color,
                        fontVariationSettings: "'FILL' 1",
                        flexShrink: 0
                      }}
                    >
                      {pct >= 60 ? 'verified' : 'trending_down'}
                    </span>
                  )}
                </div>
              )
            })}
          </div>

          <div
            style={{
              display: 'flex',
              gap: 10,
              marginTop: 22,
              flexWrap: 'wrap'
            }}
          >
            <button
              onClick={onNewExam}
              className='btn btn-primary'
              style={{ gap: 7 }}
            >
              <span
                className='material-symbols-rounded'
                style={{ fontSize: 16, fontVariationSettings: "'FILL' 1" }}
              >
                auto_awesome
              </span>
              Take a New Exam
            </button>
            <button
              onClick={onBack}
              className='btn btn-surface'
              style={{ gap: 7 }}
            >
              <span
                className='material-symbols-rounded'
                style={{ fontSize: 16 }}
              >
                arrow_back
              </span>
              Back to Setup
            </button>
          </div>
        </>
      )}
    </div>
  )
}

/* ── Main ExamPage component ────────────────────────────────────────────────── */
export default function ExamPage ({ notebooks, onGoToChat }) {
  const [view, setView] = useState('setup')
  const [genStep, setGenStep] = useState(0)
  const [examConfig, setExamConfig] = useState(null)
  const [paper, setPaper] = useState(null)
  const [answers, setAnswers] = useState({})
  const [reviewData, setReviewData] = useState(null)
  const [error, setError] = useState(null)
  const [submitErr, setSubmitErr] = useState(null)
  const [historyTarget, setHistoryTarget] = useState(null) // { id, title }
  const stepIntervalRef = useRef(null)

  /* Exam generation + review run as global background jobs (scoped "exam") so
   * they keep processing when the user navigates away and can be cancelled. */
  const { startJob, clearJob, getJob } = useJobs()
  const genJob = getJob('exam-generate', 'exam')
  const reviewJob = getJob('exam-review', 'exam')

  const notebookList = notebooks?.length
    ? notebooks
    : [{ id: 'demo', title: 'Demo Notebook', page_count: 0 }]

  /* ── Sync local view from the generate job (survives remount) ── */
  useEffect(() => {
    if (!genJob) return
    if (genJob.status === 'running') {
      setView(v => (v === 'reviewing' || v === 'review' ? v : 'generating'))
    } else if (genJob.status === 'done') {
      clearInterval(stepIntervalRef.current)
      setGenStep(
        genStepsFor(genJob.meta?.examType || examConfig?.examType).length - 1
      )
      setPaper(genJob.result)
      if (genJob.meta) setExamConfig(genJob.meta)
      setView(v => (v === 'reviewing' || v === 'review' ? v : 'paper'))
    } else if (genJob.status === 'error') {
      clearInterval(stepIntervalRef.current)
      setError(`Failed to generate exam: ${genJob.error}. Please try again.`)
      setView('setup')
    } else if (genJob.status === 'cancelled') {
      clearInterval(stepIntervalRef.current)
      setView('setup')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genJob?.status])

  /* ── Sync local view from the review job ── */
  useEffect(() => {
    if (!reviewJob) return
    if (reviewJob.status === 'running') {
      setView('reviewing')
    } else if (reviewJob.status === 'done') {
      setReviewData(reviewJob.result)
      setView('review')
    } else if (reviewJob.status === 'error') {
      setSubmitErr(`Review failed: ${reviewJob.error}`)
      setView('paper')
    } else if (reviewJob.status === 'cancelled') {
      setView('paper')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewJob?.status])

  /* ── Generate exam ── */
  const handleStart = config => {
    setExamConfig(config)
    setAnswers({})
    setError(null)
    setSubmitErr(null)
    setReviewData(null)
    clearJob('exam-review', 'exam')
    setView('generating')
    setGenStep(0)

    // Animate the step checklist while the real job runs in the background.
    const steps = genStepsFor(config.examType)
    let step = 0
    clearInterval(stepIntervalRef.current)
    stepIntervalRef.current = setInterval(() => {
      step = Math.min(step + 1, steps.length - 2)
      setGenStep(step)
    }, 900)

    startJob({
      kind: 'exam-generate',
      scopeId: 'exam',
      label: `Exam paper · ${config.marks} marks`,
      doneLabel: 'Exam paper',
      meta: config,
      run: signal =>
        api.generateExamPaper(
          config.notebookId,
          config.examType,
          config.marks,
          signal
        )
    })
  }

  /* ── Submit for review ── */
  const handleSubmit = () => {
    if (!examConfig || !paper) return
    setSubmitErr(null)
    setView('reviewing')
    startJob({
      kind: 'exam-review',
      scopeId: 'exam',
      label: 'Exam review',
      doneLabel: 'Exam review',
      run: signal =>
        api.reviewExam(examConfig.notebookId, paper, answers, signal)
    })
  }

  const handleRetake = () => {
    clearInterval(stepIntervalRef.current)
    clearJob('exam-generate', 'exam')
    clearJob('exam-review', 'exam')
    setView('setup')
    setGenStep(0)
    setPaper(null)
    setAnswers({})
    setReviewData(null)
    setError(null)
    setSubmitErr(null)
  }

  const handleViewHistory = nbId => {
    const nb = notebookList.find(n => n.id === nbId)
    setHistoryTarget({ id: nbId, title: nb?.title || 'this notebook' })
    setView('history')
  }

  /* ── No notebooks guard ── */
  if (!notebooks?.length) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 20,
          padding: 40,
          textAlign: 'center',
          background: 'var(--bg)'
        }}
      >
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            background: 'var(--amber-soft)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(245,158,11,0.2)'
          }}
        >
          <span
            className='material-symbols-rounded'
            style={{
              fontSize: 34,
              color: 'var(--amber)',
              fontVariationSettings: "'FILL' 1"
            }}
          >
            info
          </span>
        </div>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
            No notebooks yet
          </h2>
          <p style={{ color: 'var(--ink-3)', fontSize: 13, lineHeight: 1.6 }}>
            Import at least one PDF or document to generate an exam.
          </p>
        </div>
        <button onClick={onGoToChat} className='btn btn-primary btn-lg'>
          <span
            className='material-symbols-rounded'
            style={{ fontSize: 18, fontVariationSettings: "'FILL' 1" }}
          >
            upload_file
          </span>
          Go Import a Document
        </button>
      </div>
    )
  }

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'var(--bg)'
      }}
    >
      {/* Top breadcrumb bar */}
      <div
        style={{
          padding: '10px 20px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexShrink: 0
        }}
      >
        {view !== 'setup' && (
          <button onClick={handleRetake} className='btn btn-ghost btn-sm'>
            <span className='material-symbols-rounded' style={{ fontSize: 16 }}>
              arrow_back
            </span>
            Back to Setup
          </button>
        )}
        <div style={{ flex: 1 }} />

        {/* Step indicators */}
        {['setup', 'paper', 'review'].map((v, i) => {
          const labels = ['Setup', 'Exam', 'Review']
          const isDone =
            (view === 'paper' && i === 0) || (view === 'review' && i <= 1)
          const isActive =
            v === view ||
            (v === 'paper' && view === 'paper') ||
            (v === 'review' && view === 'review')
          return (
            <div
              key={v}
              style={{ display: 'flex', alignItems: 'center', gap: 4 }}
            >
              {i > 0 && (
                <div
                  style={{
                    width: 20,
                    height: 1,
                    background: 'var(--border-2)'
                  }}
                />
              )}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '4px 10px',
                  borderRadius: 'var(--radius-full)',
                  background: isActive ? 'var(--accent-soft)' : 'transparent',
                  color: isActive
                    ? 'var(--accent-2)'
                    : isDone
                    ? 'var(--green)'
                    : 'var(--ink-3)',
                  fontSize: 12,
                  fontWeight: 500,
                  border: '1px solid',
                  borderColor: isActive ? 'rgba(99,102,241,0.2)' : 'transparent'
                }}
              >
                {isDone && (
                  <span
                    className='material-symbols-rounded'
                    style={{ fontSize: 13, fontVariationSettings: "'FILL' 1" }}
                  >
                    check_circle
                  </span>
                )}
                {labels[i]}
              </div>
            </div>
          )
        })}
      </div>

      {/* Views */}
      {view === 'setup' && (
        <SetupView
          notebooks={notebookList}
          onStart={handleStart}
          onViewHistory={handleViewHistory}
          isGenerating={false}
          error={error}
        />
      )}
      {view === 'generating' && (
        <GeneratingView
          steps={genStepsFor(examConfig?.examType)}
          currentStep={genStep}
        />
      )}
      {view === 'paper' && paper && (
        <ExamPaperView
          paper={paper}
          answers={answers}
          setAnswers={setAnswers}
          onSubmit={handleSubmit}
          examType={examConfig?.examType}
          marks={examConfig?.marks}
          submitError={submitErr}
        />
      )}
      {view === 'reviewing' && <ReviewingView />}
      {view === 'review' && reviewData && (
        <ReviewView
          review={reviewData}
          notebookId={examConfig?.notebookId}
          onRetake={handleRetake}
          onChat={onGoToChat}
        />
      )}
      {view === 'history' && historyTarget && (
        <HistoryView
          key={historyTarget.id}
          notebookId={historyTarget.id}
          notebookTitle={historyTarget.title}
          onBack={handleRetake}
          onNewExam={handleRetake}
        />
      )}
    </div>
  )
}
